import {
  RestaurantSettingKey,
  type ReservationSchedule,
  type RestaurantSetting,
} from "@lemmonhq/types";
import type { RewardDays } from "@lemmonhq/types";

/**
 * Get a setting value by key from RestaurantSetting[]
 */
export const getSetting = (
  settings: RestaurantSetting[] | undefined,
  key: string,
): unknown => {
  if (!settings) return undefined;
  const setting = settings.find((s) => s.key === key);
  return setting?.value;
};

/**
 * Parse a boolean value from setting
 */
export const parseBoolean = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (value === "true" || value === "1") return true;
  return false;
};

/**
 * Parse a number value from setting
 */
export const parseNumber = (value: unknown, defaultValue: number): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  }
  return defaultValue;
};

/**
 * Parse a schedule object from setting
 */
export const parseSchedule = (value: unknown): RewardDays => {
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === "object" && parsed !== null) {
        return parsed as RewardDays;
      }
    } catch {
      return {};
    }
  }
  if (typeof value === "object" && value !== null) {
    return value as RewardDays;
  }
  return {};
};

/**
 * Get reservation schedule from settings
 */
export const getReservationSchedule = (
  settings: RestaurantSetting[] | undefined,
): ReservationSchedule => {
  const scheduleValue = getSetting(settings, "reservation_schedule");
  return parseSchedule(scheduleValue);
};

const WEEKDAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

export interface NextOpeningInfo {
  openNow: boolean;
  /** Next window start as { weekday, time } — null when schedule has no windows at all. */
  opensAt: { weekday: string; time: string; isToday: boolean } | null;
}

/**
 * Resolve a Date into { weekday, minutes-since-midnight } for a given IANA timezone
 * using Intl.DateTimeFormat, without pulling in a date library.
 */
const resolveInTimezone = (
  date: Date,
  timezone: string,
): { weekday: string; minutes: number } => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "long",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);

  const weekday =
    parts.find((p) => p.type === "weekday")?.value.toLowerCase() || "sunday";
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === "minute")?.value ?? 0);

  return { weekday, minutes: hour * 60 + minute };
};

const timeToMinutes = (time: string): number => {
  const [hour, minute] = time.split(":").map(Number);
  return hour * 60 + minute;
};

// Untyped JSON at the settings layer — a schedule range that's missing or
// mistyped start_time/end_time must never crash the booking flow. Validate
// the shape before any range is trusted, and silently drop ranges that fail.
const TIME_PATTERN = /^\d{1,2}:\d{2}$/;

const isValidRange = (
  range: unknown,
): range is { start_time: string; end_time: string } => {
  if (!range || typeof range !== "object") return false;
  const { start_time, end_time } = range as Record<string, unknown>;
  return (
    typeof start_time === "string" &&
    typeof end_time === "string" &&
    TIME_PATTERN.test(start_time) &&
    TIME_PATTERN.test(end_time)
  );
};

const getValidWindows = (
  schedule: ReservationSchedule,
  weekday: string,
): { start_time: string; end_time: string }[] => {
  const windows = schedule[weekday];
  if (!windows) return [];
  return windows.filter(isValidRange);
};

/**
 * Determine whether a restaurant is currently open for reservations based on
 * its per-weekday reservation_schedule, and if not, when it next opens.
 *
 * Fails open (openNow: true, opensAt: null) when the schedule has no windows
 * configured at all — never blocks a restaurant that hasn't set one up. A day
 * whose ranges are all malformed (missing/invalid start_time or end_time) is
 * treated the same as a day with no windows.
 */
export const getReservationOpenState = (
  schedule: ReservationSchedule | null | undefined,
  now: Date,
  timezone: string,
): NextOpeningInfo => {
  const hasAnyWindow =
    !!schedule &&
    Object.keys(schedule).some(
      (weekday) => getValidWindows(schedule, weekday).length > 0,
    );

  if (!hasAnyWindow) {
    return { openNow: true, opensAt: null };
  }

  const { weekday: todayWeekday, minutes: nowMinutes } = resolveInTimezone(
    now,
    timezone,
  );
  const todayIndex = WEEKDAYS.indexOf(todayWeekday);

  // Check whether `now` falls inside any of today's windows, including
  // windows that started yesterday and span past midnight.
  const todayWindows = getValidWindows(schedule!, todayWeekday);
  for (const range of todayWindows) {
    const startMinutes = timeToMinutes(range.start_time);
    const endMinutes = timeToMinutes(range.end_time);
    if (endMinutes <= startMinutes) {
      // Today's overnight window starts this evening. Early-morning hours
      // belong to yesterday's window, checked separately below.
      if (nowMinutes >= startMinutes) {
        return { openNow: true, opensAt: null };
      }
    } else if (nowMinutes >= startMinutes && nowMinutes < endMinutes) {
      return { openNow: true, opensAt: null };
    }
  }

  const yesterdayWeekday = WEEKDAYS[(todayIndex + 6) % 7];
  const yesterdayWindows = getValidWindows(schedule!, yesterdayWeekday);
  for (const range of yesterdayWindows) {
    const startMinutes = timeToMinutes(range.start_time);
    const endMinutes = timeToMinutes(range.end_time);
    if (endMinutes <= startMinutes && nowMinutes < endMinutes) {
      return { openNow: true, opensAt: null };
    }
  }

  // Not open now — find the next upcoming window, scanning today's
  // remaining windows first, then subsequent days (up to 7 total).
  const upcomingToday = todayWindows
    .map((range) => timeToMinutes(range.start_time))
    .filter((startMinutes) => startMinutes > nowMinutes)
    .sort((a, b) => a - b);

  if (upcomingToday.length > 0) {
    const minutes = upcomingToday[0];
    return {
      openNow: false,
      opensAt: {
        weekday: todayWeekday,
        time: formatMinutes(minutes),
        isToday: true,
      },
    };
  }

  for (let offset = 1; offset <= 7; offset++) {
    const weekday = WEEKDAYS[(todayIndex + offset) % 7];
    const windows = getValidWindows(schedule!, weekday);
    if (windows.length === 0) continue;

    const earliest = windows
      .map((range) => timeToMinutes(range.start_time))
      .sort((a, b) => a - b)[0];

    return {
      openNow: false,
      opensAt: { weekday, time: formatMinutes(earliest), isToday: false },
    };
  }

  return { openNow: false, opensAt: null };
};

const formatMinutes = (minutes: number): string => {
  const hour = Math.floor(minutes / 60) % 24;
  const minute = minutes % 60;
  return `${hour.toString().padStart(2, "0")}:${minute
    .toString()
    .padStart(2, "0")}`;
};

/**
 * Get min party size from settings
 */
export const getMinPartySize = (
  settings: RestaurantSetting[] | undefined,
): number => {
  const value = getSetting(settings, "reservations_min_party_size");
  return parseNumber(value, 1);
};

/**
 * Get max party size from settings
 */
export const getMaxPartySize = (
  settings: RestaurantSetting[] | undefined,
): number => {
  const value = getSetting(settings, "reservations_max_party_size");
  return parseNumber(value, 20);
};

/**
 * Get reservations note from settings
 */
export const getReservationsNote = (
  settings: RestaurantSetting[] | undefined,
): string => {
  const value = getSetting(settings, "reservations_note");
  return typeof value === "string" ? value : "";
};

/**
 * Get reservations enabled status from settings
 */
export const getReservationsEnabled = (
  settings: RestaurantSetting[] | undefined,
): boolean => {
  const value = getSetting(settings, "reservations_enabled");
  return parseBoolean(value);
};

/**
 * Get time slot minutes from settings
 */
export const getTimeSlotMinutes = (
  settings: RestaurantSetting[] | undefined,
): number => {
  const value = getSetting(settings, "reservations_time_slot_gap_minutes");
  return parseNumber(value, 30);
};

export const getReservationDurationMinutes = (
  settings: RestaurantSetting[] | undefined,
): number => {
  const value = getSetting(settings, "reservations_time_slot_minutes");
  return parseNumber(value, 60);
};

export const getHideReservationDurationFromGuests = (
  settings: RestaurantSetting[] | undefined,
): boolean => {
  const value = getSetting(
    settings,
    RestaurantSettingKey.RESERVATIONS_HIDE_DURATION_TO_GUESTS,
  );
  return parseBoolean(value);
};

/**
 * Get reservations require deposit status from settings
 */
export const getReservationsRequireDeposit = (
  settings: RestaurantSetting[] | undefined,
): boolean => {
  const value = getSetting(settings, "reservations_require_deposit");
  return parseBoolean(value);
};
